import { Injectable } from '@angular/core';

import { EntryPointService } from '@mirage-projects/cw-commons';
import { PcvizCollageWebFileBrowser, PcvizCollageWebFileBrowserListResult, PcvizLayer, PcvizProject } from '@mirage-projects/cw-models';
import { DataAccessPcvizService, UtilsPcvizService } from '@mirage-projects/cw-pcviz-core';

export type ReportScreenshotsFileResults = {
    volumeScreenshot: PcvizCollageWebFileBrowserListResult | undefined;
    pointCloudScreenshot: PcvizCollageWebFileBrowserListResult | undefined;
};

export type LayerIdObject = Pick<PcvizLayer, 'id'>;

/**
 * This service has the necessary business logic for download the project
 */
@Injectable({ providedIn: 'root' })
export class FileBrowserService {
    readonly temporalPath = 'downloads';
    _fileBrowser!: PcvizCollageWebFileBrowser;

    constructor(
        private dataAccessPcvizService: DataAccessPcvizService,
        private utilsPcvizService: UtilsPcvizService,
        private entryPointService: EntryPointService
    ) {}

    async download(file: PcvizCollageWebFileBrowserListResult): Promise<void> {
        await this.fileBrowser.download(file);
    }

    /**
     * Looks for a project file and starts an inmediate download.
     * @param fileName The filename of the project file to download.
     * @param project The project containing the file
     * @returns Returns the file so it can be used later for manual download if needed
     */
    async downloadFileFromProject(fileName: string, project: PcvizProject): Promise<PcvizCollageWebFileBrowserListResult | undefined> {
        const fileList = await this.fileBrowser.list(project, 'projects', this.temporalPath, undefined, true);
        const file = fileList.find((file) => file.name === fileName);
        if (file) {
            await this.download(file);
        }
        return file;
    }

    get fileBrowser(): PcvizCollageWebFileBrowser {
        if (!this._fileBrowser) {
            this._fileBrowser = this.dataAccessPcvizService.createCollageWebFileBrowser();
            this.utilsPcvizService.configureDriver(this._fileBrowser, this.entryPointService.getEntryPointsForCore());
        }
        return this._fileBrowser;
    }

    async getVolumeReportScreenshotFiles(
        layerIdObject: LayerIdObject,
        cwFileBrowser: PcvizCollageWebFileBrowser
    ): Promise<ReportScreenshotsFileResults> {
        const fileList = await cwFileBrowser.list(layerIdObject, 'layers', 'volume-screenshots');
        const volumeScreenshot = fileList.find((file) => file.name === 'volume.png');
        const pointCloudScreenshot = fileList.find((file) => file.name === 'pointCloud.png');
        return { volumeScreenshot, pointCloudScreenshot };
    }
}
